import React, { useState, useCallback, useEffect } from 'react';
import { ParametersLayout } from './ParametersLayout';
import { ui } from '../../../config/uiSizes';

interface NamePatternRow {
  id: string;
  oldName: string;
  newName: string;
  [key: string]: unknown;
}

interface NamePatternTransformationProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const NamePatternTransformation: React.FC<NamePatternTransformationProps> = ({
  parameters,
  onParametersChange
}) => {
  const [rows, setRows] = useState<NamePatternRow[]>(() => {
    const mappings = parameters?.['mappings'];

    // Handle both array format (old UI data) and object format (backend data)
    if (mappings && typeof mappings === 'object') {
      if (Array.isArray(mappings)) {
        // Array format from old UI state
        return mappings.length > 0 ? mappings : [{ id: '1', oldName: '', newName: '' }];
      } else {
        // Object format from backend - convert to array for UI
        const entries = Object.entries(mappings as Record<string, string>);
        if (entries.length > 0) {
          return entries.map(([oldName, newName], index) => ({
            id: (index + 1).toString(),
            oldName,
            newName
          }));
        }
      }
    }

    // Default to one empty row
    return [{ id: '1', oldName: '', newName: '' }];
  });

  useEffect(() => {
    if (onParametersChange) {
      // Convert array of mappings to simple key-value object for backend
      const mappingsObject = rows.reduce<Record<string, string>>((acc, row) => {
        // Only include mappings with both oldName and newName filled
        if (row.oldName && row.newName) {
          acc[row.oldName] = row.newName;
        }
        return acc;
      }, {});

      onParametersChange({ mappings: mappingsObject });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const handleAddRow = useCallback(() => {
    const newRow: NamePatternRow = {
      id: Date.now().toString(),
      oldName: '',
      newName: ''
    };
    setRows((prev) => [...prev, newRow]);
  }, []);

  const handleRemoveRow = useCallback((id: string | number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const handleUpdateRow = useCallback(
    (id: string | number, field: 'oldName' | 'newName', value: string) => {
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    },
    []
  );

  const renderRow = useCallback(
    (row: NamePatternRow, index: number, onRemove: () => void) => (
      <div className="flex items-end gap-3">
        {/* Old Name Input */}
        <div className="flex-1">
          {index === 0 && (
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Old name
            </label>
          )}
          <input
            type="text"
            value={row.oldName}
            onChange={(e) => handleUpdateRow(row.id, 'oldName', e.target.value)}
            placeholder="old-sales-queue"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Arrow */}
        <div className="flex items-center pb-2">
          <svg
            className={`${ui.svg.sm} text-gray-400 dark:text-gray-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </div>

        {/* New Name Input */}
        <div className="flex-1">
          {index === 0 && (
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              New name
            </label>
          )}
          <input
            type="text"
            value={row.newName}
            onChange={(e) => handleUpdateRow(row.id, 'newName', e.target.value)}
            placeholder="new-sales-queue"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Remove Button */}
        <button
          type="button"
          onClick={onRemove}
          className={`flex items-center gap-2 ${ui.input.custom} ${ui.text.label} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mb-0.5 ${ui.button.sm}`}
        >
          <svg className={`${ui.svg.sm} text-red-600 dark:text-red-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Remove
        </button>
      </div>
    ),
    [handleUpdateRow]
  );

  return (
    <ParametersLayout<NamePatternRow>
      title="Rename Resource(s)"
      description="Enter a list of old names and corresponding new names."
      addButtonLabel="Add mapping"
      rows={rows}
      onAddRow={handleAddRow}
      onRemoveRow={handleRemoveRow}
      renderRow={renderRow}
    />
  );
};

export default NamePatternTransformation;
